/* eslint-disable no-magic-numbers, no-underscore-dangle */
/* global window L */

import 'expose-loader?L!leaflet';
import 'leaflet-bing-layer';
import 'leaflet.pattern';
import { mapGetters } from 'vuex';

import * as types from '../../../../store/mutation-types';
import { getQueryParams, addOrReplaceQueryParams } from '../../../../helpers/fetch-helpers.js';
import { findAreaByPoint } from '../../../../helpers/leaflet-helpers';
import SelectionBox from './components/selection-box';
import Grid from './components/grid';
import MapZoom from './components/map-zoom';
import FaceMarkers from './components/face-markers';
import Pics from './components/pics';
import ProjectMarkers from './components/project-markers';

const PIC_AREA_COLOR = "#fff"
const PROJECT_LIMIT_AREA_COLOR = "#2d87ea"
const OTHER_TERRITORY_AREA_COLOR = "#0fba2b"
const HOVERED_PROJECT_LIMIT_AREA_COLOR = "#fff200"

export default {
  props: [
    'center',
    'zoom',
    'minZoom',
    'maxZoom',
    'layersZoom',
    'projectsZoom',
    'zoomLabels',
    'bingMapsKey',
    'maxNativeZoom',
    'activeProject',
    'focusedProject',
    'projectType',
    'gridEnabled',
  ],
  components: {
    'face-markers': FaceMarkers,
    'grid': Grid,
    'map-zoom': MapZoom,
    'pics': Pics,
    'project-markers': ProjectMarkers,
    'selection-box': SelectionBox,
  },
  data() {
    return {
      map: {},
      bounds: undefined,
      currentAreaGeometry: undefined,
      visiblePicAreas: [],
      visibleProjectLimitAreas: [],
      currentZoom: this.zoom,
    };
  },
  computed: {
    ...mapGetters({
      'projects': 'projectsAsArray'
    }),
    purchase() {
      return this.$store.state.map.purchase;
    },
    plotCart() {
      return this.$store.getters.plotCartAsArray;
    },
    faces() {
      return this.$store.state.map.faces;
    },
    currentArea() {
      return this.$store.getters.currentArea;
    },
    currentPicCoordSize() {
      if (this.currentArea) {
        return this.currentArea.picCoordSize;
      }

      return null;
    },
    currentReferencePoint() {
      if (this.currentArea) {
        return this.currentArea.refPoint;
      }

      return null;
    },
    mapCenter() {
      return this.$store.state.map.mapCenter;
    },
  },
  watch: {
    zoom(newValue) {
      this.map.setZoom(newValue);
    },
    focusedProject(newValue) {
      if (newValue) {
        let areas;
        if (newValue.areas.some((area) => area.areaType === 'pic')) {
          areas = newValue.areas
            .filter((area) => area.areaType === 'pic')
            .map(area => area.shape);
        } else {
          areas = newValue.areas.map(area => area.shape);
        }
        if (areas.length > 0) {
          const bounds = L.geoJSON(areas).getBounds();
          this.map.flyToBounds(bounds);
          this.$store.commit(types.FOCUS_ON_PROJECT_DONE);
        }
      }
    },
    mapCenter(newCenter) {
      if (newCenter) {
        this.map.invalidateSize();
        this.map.setView(newCenter, this.maxZoom - 2);
        this.$store.commit(types.SET_MAP_CENTER, null);
      }
    },
  },
  mounted() {
    this.map = L.map('map', {
      preferCanvas: false,
      center: this.center,
      zoom: this.zoom,
      zoomControl: false,
    });

    this.map.whenReady(() => {
      this.$emit('mapready');
    });

    this.map.on('click', (e) => {
      if (this.projectsGeoJson) {
        this.projectsGeoJson.eachLayer((layer) => {
          if (layer.getBounds().contains(e.latlng) && layer.feature) {
            const projectId = layer.feature.properties.projectId;
            this.$store.commit(types.SET_ACTIVE_PROJECT_ID, projectId);
          }
        });
      }
    });

    /*
      This feature made the interaction with the map too laggy, so it's temporarily commented.
      Setting the hover event at a layer level might help with this.
    */
    // this.map.on('mouseover', () => {
    //   this.map.on('mousemove', e => {
    //     const hoveredArea = findAreaByPoint(e.latlng, this.visibleProjectLimitAreas);
    //     if (hoveredArea) {
    //       const projectId = hoveredArea.projectId;
    //       this.visibleProjectLimitAreas.forEach(area => {
    //         if(area.projectId === projectId) {
    //           area.layer.setStyle({ color: HOVERED_PROJECT_LIMIT_AREA_COLOR });
    //           area.hovered = true;
    //         }
    //       });
    //     } else {
    //       this.visibleProjectLimitAreas.forEach(area => {
    //         if(area.hovered) {
    //           area.layer.setStyle({ color: PROJECT_LIMIT_AREA_COLOR });
    //           area.hovered = false;
    //         }
    //       });
    //     }
    //   });
    // });

    L.tileLayer.bing({
      maxZoom: this.maxZoom,
      minZoom: this.minZoom,
      imagerySet: 'AerialWithLabels',
      maxNativeZoom: this.maxNativeZoom,
      bingMapsKey: this.bingMapsKey,
    }).addTo(this.map);

    this.$store.dispatch('initMapProjects', { projectType: this.projectType }).then(() => {
      const projectsGeoJson = this._getProjectsGeoJson();
      const styles = {
        'pic': { color: PIC_AREA_COLOR, weight: 3, fillOpacity: 0, dashArray: '10, 5' },
        'project_limit': { color: PROJECT_LIMIT_AREA_COLOR, weight: 2, fillOpacity: 0.05, dashArray: '10, 5' },
        'other_territory': { color: OTHER_TERRITORY_AREA_COLOR, weight: 3, fillOpacity: 0, dashArray: '10, 5' },
      };

      this.projectsGeoJson = L.geoJson(projectsGeoJson, {
        style: function(area) {
          return styles[area.areaType];
        },
      }).addTo(this.map);

      const params = getQueryParams();
      if (params.center && params.zoom) {
        const center = params.center.split(',');
        this.map.setView(center, params.zoom);
      }

      this.updateMap();
      this._updatePurchaseIfInParams(params);

      this.map.on('moveend', this.updateMap);
    });

    // Add temporary marker
    L.Icon.Default.imagePath = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.0.3/images/';
  },
  methods: {
    updateMap() {
      this.bounds = this.map.getBounds();
      this.currentZoom = this.map.getZoom();
      const limit = (this.currentZoom >= this.layersZoom) ? null : 100;
      this.$store.commit(types.SET_BOUNDS, this.bounds);
      this.$store.commit(types.SET_LIMIT, limit);
      this.$store.commit(types.SET_CURRENT_ZOOM, this.currentZoom);
      if (this.currentZoom >= this.projectsZoom) {
        this.$store.dispatch('getFaces');
      }
      const currentInfo = this._getCurrentProjectAndArea();
      this._updateVisibleAreas();
      this.$store.commit(types.SET_CURRENT_AREA_ID, currentInfo.areaId);
      if (currentInfo.projectId) this.$store.commit(types.SET_ACTIVE_PROJECT_ID, currentInfo.projectId);
      const url = this._generateURLParams();
      window.history.replaceState(
        {
          zoom: this.currentZoom,
          center: this.bounds.getCenter(),
        }, '', url);
    },
    _generateURLParams() {
      const mapCenter = this.bounds.getCenter();
      const params = {
        center: `${mapCenter.lat},${mapCenter.lng}`,
        zoom: this.currentZoom,
      };

      return addOrReplaceQueryParams(location.origin + location.pathname, params);
    },
    showGrid() {
      return (this.currentZoom > this.layersZoom) && this.gridEnabled;
    },
    showFaceMarkers() {
      return this.currentZoom <= this.layersZoom && this.currentZoom >= this.projectsZoom;
    },
    showPics() {
      return this.currentZoom > this.layersZoom && this.currentZoom >= this.projectsZoom;
    },
    showLogos() {
      return this.currentZoom < this.projectsZoom;
    },
    _getCurrentProjectAndArea() {
      if (!this.projectsGeoJson) return null;
      const projects = [];
      let projectId;
      let areaId;
      this.projectsGeoJson.eachLayer((project) => {
        if (this.bounds.intersects(project.getBounds())) {
          projects.push(project);
          areaId = project.feature.properties.areaId;
          this.currentAreaGeometry = L.geoJSON(project.feature);
        }
      });

      if (projects.length === 1) {
        projectId = projects[0].feature.properties.projectId;
      }

      return { areaId, projectId };
    },
    _getProjectsGeoJson() {
      const projectsGeoJson = [];
      this.$store.getters.projectsAsArray.forEach(project => {
        const projectShape = project.areas
          .map(area => {
            const areaJson = area.shape;
            areaJson.properties.areaId = area.id;
            areaJson.properties.projectId = project.id;
            areaJson.properties.refPoint = area.refPoint;
            areaJson.properties.picCoordSize = area.picCoordSize;
            areaJson.areaType = area.areaType;

            return areaJson;
          });
        projectsGeoJson.push(...projectShape);
      });

      return projectsGeoJson;
    },
    _updateVisibleAreas() {
      if (!this.projectsGeoJson) return null;
      this.visiblePicAreas = [];

      this.projectsGeoJson.eachLayer((layer) => {
        if (this.bounds.intersects(layer.getBounds())) {
          if (layer.feature.areaType === 'pic') {
            this.visiblePicAreas.push({
              projectId: layer.feature.properties.projectId,
              areaId: layer.feature.properties.areaId,
              refPoint: layer.feature.properties.refPoint,
              picCoordSize: layer.feature.properties.picCoordSize,
              geometry: L.geoJSON(layer.feature),
            });
          } else if (layer.feature.areaType === 'project_limit') {
            this.visibleProjectLimitAreas.push({
              projectId: layer.feature.properties.projectId,
              geometry: L.geoJSON(layer.feature),
              layer: layer,
              hovered: false,
            });
          }
        }
      });

      return this.visiblePicAreas;
    },
    _updatePurchaseIfInParams(params) {
      if (params.purchaseId && params.purchaseToken) {
        this.$store.dispatch('updatePurchase', { id: params.purchaseId, token: params.purchaseToken });
      }
    },
  },
};
